import { useCallback, useEffect, useState } from 'react';
import { Role2, User } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Button, Modal, Table, Typography, Select, Input, message } from 'antd';
import { ExclamationCircleOutlined, PlusOutlined, SearchOutlined, CloseOutlined } from '@ant-design/icons';

export function Role2UsersForm(props: { role?: Role2 }) {
  const { Text } = Typography;
  const [allUsers, setAllUsers] = useState<User[]>();
  const [users, setUsers] = useState<User[]>();
  const [usersLoading, setUsersLoading] = useState(false);
  const [allUsersLoading, setAllUsersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [searchText, setSearchText] = useState('');

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    apiClient
      .get(`authorization/roles/${props.role?.id}/users`)
      .then((response: any) => {
        setUsers(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setUsersLoading(false);
      });
  }, [props.role?.id]);

  useEffect(() => {
    if (props.role?.id) {
      loadUsers();
    }
  }, [loadUsers, props.role?.id]);

  const loadAllUsers = () => {
    if (allUsers) return; // Don't reload if already loaded

    setAllUsersLoading(true);
    apiClient
      .get('users')
      .then((response: any) => {
        setAllUsers(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setAllUsersLoading(false);
      });
  };

  const handleAddModalOpen = () => {
    setAddModalVisible(true);
    setSelectedUserIds([]);
    loadAllUsers();
  };

  const confirmDeleteUser = (userId: number) => {
    Modal.confirm({
      title: 'Kullanıcı sil',
      icon: <ExclamationCircleOutlined />,
      content: 'Kullanıcıyı rolden silmek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Vazgeç',
      onOk: () => removeUser(userId),
    });
  };

  const removeUser = (userId: number) => {
    setDeleting(true);
    apiClient
      .delete(`authorization/roles/${props.role?.id}/users/${userId}`)
      .then(() => {
        message.success('Kullanıcı başarıyla silindi');
        loadUsers();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setDeleting(false);
      });
  };

  const addUsers = () => {
    if (!selectedUserIds.length) return;

    setSubmitting(true);
    apiClient
      .post(`authorization/roles/user-assignments`, { role2Ids: [props.role?.id], userIds: selectedUserIds })
      .then(() => {
        message.success(`${selectedUserIds.length} kullanıcı başarıyla eklendi`);
        setAddModalVisible(false);
        setSelectedUserIds([]);
        loadUsers();
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const tableColumns = [
    {
      title: 'İsim',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Departman',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '',
      key: 'action',
      render: (_: any, record: User) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CloseOutlined
            className="text-red-500 cursor-pointer hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => !deleting && confirmDeleteUser(record.id)}
            style={{ opacity: deleting ? 0.5 : 1 }}
            disabled={deleting}
          />
        </div>
      ),
    },
  ];

  const filteredUsers = users?.filter(
    user =>
      user.fullName.toLowerCase().includes(searchText.toLowerCase()) ||
      user.email.toLowerCase().includes(searchText.toLowerCase()) ||
      user.department?.toLowerCase().includes(searchText.toLowerCase()),
  );

  const availableUsers = allUsers?.filter((user: User) => !users?.some((assignedUser: User) => assignedUser.id === user.id));

  return (
    <div>
      <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between' }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAddModalOpen}>
          Kullanıcı ekle
        </Button>
        <div>
          <Input
            placeholder="Arama"
            prefix={<SearchOutlined />}
            onChange={e => setSearchText(e.target.value)}
            style={{ width: 250 }}
            allowClear
          />
        </div>
      </div>

      <Table columns={tableColumns} dataSource={filteredUsers} rowKey="id" loading={usersLoading} pagination={false} />

      <Modal
        title="Kullanıcı ekle"
        visible={addModalVisible}
        onOk={addUsers}
        onCancel={() => {
          setAddModalVisible(false);
          setSelectedUserIds([]);
        }}
        okButtonProps={{ disabled: !selectedUserIds.length, loading: submitting }}
      >
        <Select
          mode="multiple"
          showSearch
          style={{ width: '100%' }}
          placeholder="Kullanıcı seçin"
          onChange={value => setSelectedUserIds(value)}
          value={selectedUserIds}
          loading={allUsersLoading}
          filterOption={(input, option) => {
            const user = availableUsers?.find(u => u.id === option?.value);
            if (!user) return false;
            return (
              user.fullName.toLowerCase().includes(input.toLowerCase()) ||
              user.email.toLowerCase().includes(input.toLowerCase()) ||
              user.department?.toLowerCase().includes(input.toLowerCase())
            );
          }}
        >
          {availableUsers?.map(user => (
            <Select.Option key={user.id} value={user.id}>
              {user.fullName} ({user.email})
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </div>
  );
}
